import useSWR from 'swr';
import { iiifFetchQueue } from '../utils/fetchQueue';

// Track URLs that have failed — retries get priority queue placement
const failedUrls = new Set();

// Scholten serves images from Leiden by default; if that fails, the second
// argument lets the caller specify a NAS opaque-ID URL to try instead.
// Pass null for non-Scholten items (and for skip-fetching).
const useCachedImage = (imageUrl, fallbackUrl) => {
  const primary = useSWR(imageUrl, (url) => {
    const isPriority = failedUrls.has(url);
    if (isPriority) failedUrls.delete(url);
    return iiifFetchQueue((signal) =>
      fetch(url, { signal }).then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch image: ${url}`);
        return res.blob();
      })
    , isPriority);
  },
    {
      errorRetryCount: 2,
      errorRetryInterval: 2000,
      dedupingInterval: 60000,
      onErrorRetry: (err, key, _config, revalidate, { retryCount }) => {
        if (err?.name === 'AbortError' || err?.message === 'Queue cleared' || err?.message === 'Queue timeout') return;
        if (retryCount >= 2) return;
        failedUrls.add(key);
        setTimeout(() => revalidate({ retryCount }), 2000);
      },
    }
  );

  // Activate the fallback fetch only when the primary errors. Passing null to
  // useSWR is the canonical way to skip a fetch, so the fallback never even
  // enters the queue unless we actually need it.
  const shouldUseFallback = !!primary.error && !!fallbackUrl;
  const fallback = useSWR(shouldUseFallback ? fallbackUrl : null, (url) => {
    const isPriority = failedUrls.has(url);
    if (isPriority) failedUrls.delete(url);
    return iiifFetchQueue((signal) =>
      fetch(url, { signal }).then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch image: ${url}`);
        return res.blob();
      })
    , isPriority);
  },
    {
      errorRetryCount: 2,
      errorRetryInterval: 2000,
      dedupingInterval: 60000,
      onErrorRetry: (err, key, _config, revalidate, { retryCount }) => {
        if (err?.name === 'AbortError' || err?.message === 'Queue cleared' || err?.message === 'Queue timeout') return;
        if (retryCount >= 2) return;
        failedUrls.add(key);
        setTimeout(() => revalidate({ retryCount }), 2000);
      },
    }
  );

  if (shouldUseFallback) {
    return {
      image: fallback.data,
      isLoading: !fallback.error && !fallback.data,
      isError: !!fallback.error,
    };
  }
  return {
    image: primary.data,
    isLoading: !primary.error && !primary.data,
    isError: !!primary.error,
  };
};

export default useCachedImage;
